import { z } from 'zod';
/* ═══════════════════════════════════════════════════════════════════════
 * COURSE EXERCISES
 *
 * The course was written as Markdown by people, not generated from a form,
 * so the taxonomy below describes what is actually in the files rather than
 * what would be tidy. Roughly seven exercises in ten fall into the first
 * four shapes; the rest are long-tail.
 *
 * `unknown` is load-bearing, not a failure case. 254 hand-written exercises
 * will never all parse, and without a type that simply keeps the original
 * Markdown the parser becomes a blocker for the whole feature. With it, the
 * course ships at whatever coverage the parser reaches today and improves
 * without anyone waiting.
 * ═══════════════════════════════════════════════════════════════════════ */
export const EXERCISE_TYPES = [
    'true_false',
    'choice',
    'gap',
    'gap_bank',
    'match',
    'transform',
    'sort_groups',
    'order_words',
    'odd_one_out',
    'open',
    'unknown',
];
/** Types a student's answer can be marked against without a human. */
export const AUTO_GRADABLE = new Set([
    'true_false',
    'choice',
    'gap',
    'gap_bank',
    'match',
    'transform',
    'sort_groups',
    'order_words',
    'odd_one_out',
]);
/**
 * One numbered line of an exercise.
 *
 * `answers` holds every spelling that counts as right — the answer key
 * writes alternatives as `robił / robiła`, and a course that rejects the
 * feminine form of a past tense it just taught would be worse than no
 * marking at all.
 *
 * `note` is the parenthetical the key sometimes carries — "F (mieszka w
 * Warszawie)" — which is the explanation, and the most useful thing on the
 * screen the moment somebody gets it wrong.
 */
export const exerciseItemSchema = z.object({
    n: z.number().int().positive(),
    prompt: z.string(),
    answers: z.array(z.string()).default([]),
    note: z.string().default(''),
});
const base = { items: z.array(exerciseItemSchema).default([]) };
export const exercisePayloadSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('true_false'), ...base }),
    /** `options` are the a/b/c texts; the answer is the letter. */
    z.object({ type: z.literal('choice'), ...base, options: z.array(z.array(z.string())).default([]) }),
    /**
     * `passage` is set when the gaps sit inside a text, a table or a note —
     * "(3) ________" — instead of on numbered lines: the student reads the
     * passage and answers per gap number. Optional, not defaulted, so the
     * payloads of every other gap exercise stay byte-identical (their hash is
     * what keeps a student's attempts from going stale).
     */
    z.object({ type: z.literal('gap'), ...base, passage: z.string().optional() }),
    /** The words offered above the gaps. Shown as chips to tap. */
    z.object({ type: z.literal('gap_bank'), ...base, bank: z.array(z.string()).default([]), passage: z.string().optional() }),
    /** `bank` is the lettered right-hand column: ["A dzień dobry", …]. */
    z.object({ type: z.literal('match'), ...base, bank: z.array(z.string()).default([]) }),
    z.object({ type: z.literal('transform'), ...base }),
    z.object({ type: z.literal('sort_groups'), ...base, groups: z.array(z.string()).default([]) }),
    z.object({ type: z.literal('order_words'), ...base }),
    z.object({ type: z.literal('odd_one_out'), ...base, options: z.array(z.array(z.string())).default([]) }),
    /** Writing and speaking. The key carries a model answer or a marking rubric. */
    z.object({ type: z.literal('open'), ...base, guidance: z.string().default('') }),
    /** Parsed enough to show, not enough to mark. Rendered from `raw`. */
    z.object({ type: z.literal('unknown') }),
]);
/**
 * The same payload with every answer removed, which is the only shape that
 * may reach a browser.
 *
 * The answers live in the same jsonb column as the questions, so without
 * this the key is one Network tab away and "auto-marked exercises" is
 * decoration. There is a test asserting no variant survives with answers.
 */
export function stripAnswers(payload) {
    if (payload.type === 'unknown')
        return payload;
    const items = payload.items.map((i) => ({ ...i, answers: [], note: '' }));
    if (payload.type === 'open')
        return { ...payload, items, guidance: '' };
    return { ...payload, items };
}
